import React, {
  PropsWithChildren,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Accel } from "../../api/accel";
import {
  ColorsPaletteOption,
  AccelThemeConfigColors,
} from "../../types/AccelContext";
import { add, isBefore } from "date-fns";
import {
  AccelContextActionName,
  AccelContextAction,
} from "../../types/AccelContext";

import { AccelContext } from "../../contexts/AccelContext/AccelContext";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import useSWR, { SWRConfig } from "swr";
import TransactionsProvider from "../TransactionsProvider";
import dayjs from "dayjs";
import AccountsProvider from "../AccountsProvider";
import { getGeneralLedger } from "../../api/accel/generalledger";
import { Company } from "../../types/Company";
import { Category } from "../../types/Category";
import {
  ProfitAndLossData,
  ProfitAndLossDataYearly,
} from "../../types/ProfitAndLoss";
import { RevenueProfitExpenseResponse } from "../../types/revenueprofitexpenseResponse";
import { TransactionsByMonth } from "../../types/Transaction";
type AccelEnvConfig = {
  url: string;
  apiUrl: string;
};

export type Props = {
  companyId: string;
  enterpriseId?: string;
  enterpriseSecret?: string;
  accessToken?: string;
  environment?: keyof typeof AccelEnvironment;
  theme?: AccelThemeConfigColors;
  usePlaidSandbox?: boolean;
  fillerData?: boolean;
  colors?: ColorsPaletteOption;
  apiUrl?: string;
};

export const AccelEnvironment: Record<string, AccelEnvConfig> = {
  production: {
    url: "not defined yet",
    apiUrl: "not defined yet",
  },
  development: {
    url: "dev-sgghwrhkwi5ubfpz.us.auth0.com",
    apiUrl: "http://localhost:8080",
  },
};

export const AccelProvider = ({
  companyId,
  enterpriseId,
  enterpriseSecret,
  accessToken,
  environment = "development",
  theme,
  fillerData,
  children,
  apiUrl: givenApiUrl,
}: PropsWithChildren<Props>) => {
  const defaultSWRConfig = {
    revalidateInterval: 0,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
  };
  // Cast theme to the correct type
  const { url } = AccelEnvironment[environment];
  const apiUrl = givenApiUrl || AccelEnvironment[environment].apiUrl;

  const [auth, setAuth] = useState({
    access_token: "",
    token_type: "",
    expires_in: 0,
    expires_at: new Date(2000, 1, 1),
  });

  const [company, setCompany] = useState<Company | undefined>(undefined);
  const [categories, setCategories] = useState<Category[]>([]);
  const [generalLedger, setGeneralLedger] = useState<
    Record<string, any> | undefined
  >(undefined);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [profitAndLoss, setProfitAndLoss] = useState<
    ProfitAndLossData | undefined
  >(undefined);
  const [incomeStatementSheet, setIncomeStatementSheet] = useState<
    Record<string, any> | undefined
  >(undefined);
  const [profitandlossYearly, setProfitAndLossYearly] = useState<
    ProfitAndLossDataYearly[] | undefined
  >(undefined);
  const [fillerDataState, setFillerData] = useState(fillerData ?? false);
  const [revenueProfitExpense, setRevenueProfitExpense] = useState<
    RevenueProfitExpenseResponse | undefined
  >(undefined);
  const [transactionsByMonth, setTransactionsByMonth] = useState<
    TransactionsByMonth | undefined
  >(undefined);

  const dispatch = useCallback((action: AccelContextAction) => {
    switch (action.type) {
      case AccelContextActionName.setAuth:
        setAuth(action.payload.auth);
        break;
      case AccelContextActionName.setCompany:
        setCompany(action.payload.company);
        break;
      case AccelContextActionName.setCategories:
        setCategories(action.payload.categories);
        break;
      case AccelContextActionName.setSelectedDate:
        setSelectedDate(action.payload.selectedDate);
        break;
      case AccelContextActionName.setProfitAndLoss:
        setProfitAndLoss(action.payload.profitandloss);
        break;
      case AccelContextActionName.setFillerData:
        setFillerData(action.payload.fillerData);
        break;
      case AccelContextActionName.setGeneralLedger:
        setGeneralLedger(action.payload.generalLedger);
        break;
      case AccelContextActionName.setIncomeStatementSheet:
        setIncomeStatementSheet(
          action.payload.incomeStatementSheet as Record<string, any>
        );
        break;

      case AccelContextActionName.setProfitAndLossYearly:
        setProfitAndLossYearly(action.payload.profitandlossYearly);
        break;
      case AccelContextActionName.setRevenueProfitExpense:
        setRevenueProfitExpense(action.payload.revenueProfitExpense);
        break;
      case AccelContextActionName.setTransactionsByMonth:
        setTransactionsByMonth(
          action.payload.transactionsByMonth as TransactionsByMonth
        );
        break;
      default:
        break;
    }
  }, []);

  const [authErrorState, setAuthError] = useState<boolean>(false);
  
  const { data: authData, error: authError } =
    enterpriseId !== undefined && enterpriseSecret !== undefined
      ? useSWR(
          accessToken === undefined &&
            enterpriseId !== undefined &&
            enterpriseSecret !== undefined &&
            isBefore(auth.expires_at, new Date()) &&
            "authenticate",
          Accel.authenticate({
            enterpriseId,
            enterpriseSecret,
            authenticationUrl: url,
          }),
          defaultSWRConfig
        )
      : { data: undefined, error: undefined };

  useEffect(() => {
    if (accessToken) {
      dispatch({
        type: AccelContextActionName.setAuth,
        payload: {
          auth: {
            access_token: accessToken,
            token_type: "Bearer",
            expires_in: 3600,
            expires_at: add(new Date(), { seconds: 3600.0 }),
          },
        },
      });
    } else if (authData?.access_token) {
      dispatch({
        type: AccelContextActionName.setAuth,
        payload: {
          auth: {
            ...authData,
            expires_at: add(new Date(), { seconds: authData.expires_in }),
          },
        },
      });
    } else if (authError) {
      setAuthError(true);
    }
  }, [accessToken, authData?.access_token, authError]);

  if (authError) {
    return null; // or return <></> for an empty fragment
  }

  const { data: transactionsByMonthData } = useSWR(
    auth.access_token ? "transactions-by-month" : null,
    Accel.getTransactionsMonth(apiUrl, auth.access_token, {
      params: { companyId },
    }),
    defaultSWRConfig
  );
  useEffect(() => {
    if (transactionsByMonthData) {
      dispatch({
        type: AccelContextActionName.setTransactionsByMonth,
        payload: { transactionsByMonth: transactionsByMonthData },
      });
    }
  }, [transactionsByMonthData]);

  const { data: companyData } = useSWR(
    auth.access_token ? "company" : null,
    Accel.getCompany(apiUrl, auth.access_token, {
      params: { companyId },
    }),
    defaultSWRConfig
  );

  useEffect(() => {
    if (companyData) {
      dispatch({
        type: AccelContextActionName.setCompany,
        payload: { company: companyData },
      });
    }
  }, [companyData]);

  const { data: categoriesData } = useSWR(
    auth.access_token ? "categories" : null,
    Accel.getCategories(apiUrl, auth.access_token, {
      params: { companyId },
    }),
    defaultSWRConfig
  );

  useEffect(() => {
    if (categoriesData) {
      dispatch({
        type: AccelContextActionName.setCategories,
        payload: { categories: categoriesData },
      });
    }
  }, [categoriesData]);

  // Modify the generalLedger useSWR hook
  const { data: generalLedgerData } = useSWR(
    auth.access_token ? [`general-ledger-${companyId}`, selectedDate] : null,
    getGeneralLedger(apiUrl, auth.access_token, {
      params: {
        companyId,
        month: selectedDate.format("MM"),
        year: selectedDate.format("YYYY"),
      },
    }),
    defaultSWRConfig
  );

  useEffect(() => {
    if (generalLedgerData) {
      dispatch({
        type: AccelContextActionName.setGeneralLedger,
        payload: { generalLedger: generalLedgerData },
      });
    }
  }, [generalLedgerData, selectedDate, dispatch]);

  // Modify the incomeStatementSheet useSWR hook
  const { data: incomeStatementSheetData } = useSWR(
    auth.access_token ? ["income-statement-sheet", selectedDate] : null,
    Accel.getIncomeStatementSheet(apiUrl, auth.access_token, {
      params: {
        companyId,
        month: selectedDate.format("MM"),
        year: selectedDate.format("YYYY"),
      },
    }),
    defaultSWRConfig
  );

  useEffect(() => {
    if (incomeStatementSheetData) {
      dispatch({
        type: AccelContextActionName.setIncomeStatementSheet,
        payload: { incomeStatementSheet: incomeStatementSheetData },
      });
    }
  }, [incomeStatementSheetData, selectedDate, dispatch]);

  // Modify the profitAndLossYearly useSWR hook
  const { data: responseData } = useSWR(
    auth.access_token ? ["profit&loss&yearly", selectedDate] : null,
    Accel.getProfitAndLossYearly(apiUrl, auth.access_token, {
      params: { companyId, year: selectedDate.year().toString() },
    }),
    defaultSWRConfig
  );

  useEffect(() => {
    if (responseData !== undefined) {
      dispatch({
        type: AccelContextActionName.setProfitAndLossYearly,
        payload: { profitandlossYearly: responseData },
      });
    }
  }, [responseData, selectedDate, dispatch]);

  // Modify the revenueProfitExpense useSWR hook
  const { data: revenueProfitExpenseData } = useSWR(
    auth.access_token ? ["revenue-profit-expense", selectedDate] : null,
    Accel.getRevenueProfitExpense(apiUrl, auth.access_token, {
      params: {
        companyId,
        year: selectedDate.format("YYYY"),
        month: selectedDate.format("MM"),
      },
    }),
    defaultSWRConfig
  );

  useEffect(() => {
    if (revenueProfitExpenseData) {
      dispatch({
        type: AccelContextActionName.setRevenueProfitExpense,
        payload: { revenueProfitExpense: revenueProfitExpenseData },
      });
    }
  }, [revenueProfitExpenseData, selectedDate, dispatch]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <SWRConfig
        value={{
          provider: () => new Map(),
          shouldRetryOnError: false,
        }}
      >
        <AccelContext.Provider
          value={{
            auth,
            companyId,
            company,
            categories,
            apiUrl,
            theme: theme as AccelThemeConfigColors,
            onboardingStep: undefined,
            environment,
            selectedDate,
            dispatcher: dispatch,
            profitandloss: profitAndLoss,
            fillerData: fillerDataState,
            generalLedger,
            incomeStatementSheet,
            revenueProfitExpense,
            profitandlossYearly,
            transactionsByMonth,
            authErrorState,
          }}
        >
          <TransactionsProvider>
            <AccountsProvider>{children}</AccountsProvider>
          </TransactionsProvider>
        </AccelContext.Provider>
      </SWRConfig>
    </LocalizationProvider>
  );
};

export default AccelProvider;
